import { useMemo } from 'react';
import { useUrlParams } from '#hooks/useUrlParams.ts';

export const HOST_FILTERS = [
  'type',
  'updateAvailable',
  'isBlocked',
  'unenrolled',
  'mobile',
  'networkID',
  'roleID',
  'endpointOIDCUserID',
  'tags',
] as const;

const TAG_FILTER_KEY = 'filter.tags' as const;
// The user-facing search uses the conventional `q` URL param (shorter, recognizable, decoupled from
// the API). The API wire param stays `filter.search` — see translateFilterToAPI in hosts.ts.
const SEARCH_PARAM_KEY = 'q' as const;
const PARAM_SCHEMA = {
  cursor: 'scalar',
  'filter.type': 'scalar',
  'filter.updateAvailable': 'scalar',
  'filter.isBlocked': 'scalar',
  'filter.unenrolled': 'scalar',
  'filter.mobile': 'scalar',
  'filter.networkID': 'scalar',
  'filter.roleID': 'scalar',
  'filter.endpointOIDCUserID': 'scalar',
  'filter.tags': 'array',
  q: 'scalar',
} as const;

/**
 * Handles reading host filter values from the URL and returning them as an object
 * which can be passed to our query functions.
 */
export function useHostFilters() {
  const [params, setParams] = useUrlParams(PARAM_SCHEMA);
  const { cursor } = params;

  // `params` is memoized by useUrlParams (stable until the URL changes). Derive the memos below from it
  // directly rather than a `{ cursor, ...rest }` rest-spread, which would allocate a fresh object every
  // render and recompute `tags`/`filters` (and re-render their consumers) every time.
  const tags = useMemo(() => params[TAG_FILTER_KEY] ?? [], [params]);

  // `search` is surfaced on its own rather than folded into `filters` so callers can manage the
  // search input independently of the filter bag (and so it doesn't count toward `appliedFilters`).
  const search = params[SEARCH_PARAM_KEY] ?? '';
  // Changing the search term resets the cursor so we don't page into a stale result set. Setting both
  // params in one call avoids two setParams writes racing on a stale `searchParams` closure. We replace
  // (rather than push) so debounced keystrokes don't flood the history stack with one entry per change.
  const setSearch = (value: string) =>
    setParams({ [SEARCH_PARAM_KEY]: value || undefined, cursor: undefined }, { replace: true });

  // It is safe to make this assertion because `useUrlParams` filters out any other params we haven't specified
  const filters = useMemo(() => {
    const scalar = Object.fromEntries(
      Object.entries(params)
        .filter(([name]) => name !== 'cursor' && name !== TAG_FILTER_KEY && name !== SEARCH_PARAM_KEY)
        .map(([name, value]) => [
          name.replace(/^filter\./, ''),
          value === 'true' || (value !== 'false' && value) || undefined,
        ])
    ) as {
      [N in keyof typeof params as N extends 'cursor' | typeof TAG_FILTER_KEY | typeof SEARCH_PARAM_KEY ? never
      : N extends string ? StripFilter<N>
      : N]: true | string | undefined;
    };
    return { ...scalar, ...(tags.length > 0 ? { tags } : {}) };
  }, [params, tags]);

  const setCursor = (cur?: string) => {
    /**
     * We want synchronous nav here, otherwise it takes too long for the cursor set state to settle. With `v7_startTransition` each navigation is wrapped with `startTransition` marking the navigation as a state update in the background, rather than blocking: <https://react.dev/reference/react/startTransition>
     *
     * TODO: Adjust this to work with startTransition and not require the flushSync.
     */
    setParams({ cursor: cur }, { flushSync: true });
  };

  return { cursor, setCursor, setParams, filters, tags, search, setSearch };
}

// This will remove the `filter.` from the start of a string type
type StripFilter<S extends string> = S extends `filter.${infer Tail}` ? `${Tail}` : S;
